// Painel de Efetivo — retrato atual do quadro de pessoal.
//
// Somente leitura, sem filtros: o escopo vem do perfil (o backend lê a lotação
// de quem consulta). A definição dos números mora no backend
// (services/paineis/); esta tela só desenha. Sem lista nominal por decisão.
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBBtn, MDBSpinner, MDBIcon } from 'mdbreact';

import NavBar from '../../../components/NavBar';
import api from '../../../services/api';
import {
  TabelaUnidades, NotaMetodologica, BarraComposicaoTotal, ListaBarras, formataNumero, formataDataHora,
} from '../../../components/Painel';

// Par de destaque: azul = força (o ponto), cinza = demais (contexto).
// `classe` permite a textura de impressão no segmento de contexto.
const SERIES = [
  { rotulo: 'Força de segurança', curto: 'Força', cor: 'var(--painel-a)', classe: 'painel-a' },
  { rotulo: 'Demais servidores', curto: 'Demais', cor: 'var(--painel-b)', classe: 'painel-b' },
];

const PENDENCIAS = [
  ['sem_funcao', 'Ativos sem função (sem vaga aberta)'],
  ['sem_lotacao', 'Ativos sem lotação'],
  ['gerencia_inativa', 'Lotados em gerência desativada'],
  ['adido_sem_orgao', 'Marcados como cedidos, sem órgão de origem'],
  ['fora_da_operacao', 'Lotados em gerência desativada fora da operação (ex.: de teste)'],
  ['sem_perfil', 'Ativos sem perfil de acesso'],
];

// Adesão aos perfis de acesso: a meta é todo usuário ter um (usuario.perfil_id).
const SERIES_PERFIL = [
  { rotulo: 'Com perfil', cor: 'var(--painel-a)', classe: 'painel-a' },
  { rotulo: 'Sem perfil', cor: 'var(--painel-b)', classe: 'painel-b' },
];

const paraGrupos = (unidades) => {
  const linha = (u) => ({
    chave: String(u.id),
    rotulo: u.nome,
    marca: u.ativa ? null : 'gerência desativada',
    a: u.forca,
    b: u.demais,
    total: u.total,
    filhos: u.alas.map((al) => ({
      chave: `${al.nome}|${al.escala}`, rotulo: al.nome, detalhe: al.escala, a: al.forca, b: al.demais, total: al.total,
    })),
  });
  return [
    { titulo: 'Postos fiscais', linhas: unidades.filter((u) => u.posto_fiscal).map(linha) },
    { titulo: 'Unidades de apoio', linhas: unidades.filter((u) => !u.posto_fiscal).map(linha) },
  ];
};

const rotuloOrgao = (o) => (o.orgao === 'Casa Civil' ? 'Casa Civil (comissionados)' : o.orgao);

// Definição de cada número — abre pelo botão de ajuda do cabeçalho.
const NOTA = [
  {
    icone: 'database',
    titulo: 'Fonte',
    texto: 'Banco de dados do SIG, consultado no momento em que a tela é aberta (horário no topo).',
  },
  {
    icone: 'users',
    titulo: 'Universo',
    texto: 'Servidores com cadastro ativo no SIG, dentro da sua área de visão.',
  },
  {
    icone: 'shield',
    titulo: 'Força de segurança × demais servidores',
    texto: (
      <>
        <strong>Força de segurança</strong> é quem pode usar arma, identificado pelo órgão de origem do servidor
        cedido: PMERJ, Bombeiros (CBMERJ), Polícia Civil (PCERJ), Polícia Penal (SEAP), PF, PRF e Forças Armadas.
        O SIG não registra porte individual. <strong>Demais servidores</strong> são os comissionados da Casa Civil
        e os cedidos de órgãos civis (ex.: SEFAZ, TURISRIO, INEA).
      </>
    ),
  },
  {
    icone: 'calendar-alt',
    titulo: 'Lotação não é presença',
    texto: 'Férias, licenças e afastamentos continuam contados na lotação.',
  },
  {
    icone: 'briefcase',
    titulo: 'Função',
    texto: 'A da vaga aberta do servidor. Sem vaga aberta, aparece como "(sem função)".',
  },
];

export default function PainelEfetivo() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null); // 'sem-acesso' | 'falha' | null

  const carrega = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const { data } = await api.get('/paineis/efetivo');
      setDados(data);
    } catch (e) {
      const status = e && e.response && e.response.status;
      if (status === 403) {
        setErro('sem-acesso');
      } else {
        setErro('falha');
        toast.error('Não foi possível carregar o painel de efetivo.');
      }
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carrega(); }, [carrega]);

  const corpo = () => {
    if (carregando && !dados) return <div className="text-center my-5"><MDBSpinner /></div>;
    if (erro === 'sem-acesso') {
      return <p className="my-5 text-center">Você não tem acesso a este painel, ou não está lotado em nenhuma gerência.</p>;
    }
    if (erro === 'falha' && !dados) {
      return <div className="text-center my-5"><MDBBtn color="primary" onClick={carrega}>Tentar de novo</MDBBtn></div>;
    }
    const { totais, unidades, orgaos, funcoes, perfis, pendencias } = dados;
    const semPerfilPorUnidade = unidades
      .filter((u) => u.sem_perfil > 0)
      .map((u) => ({ rotulo: u.nome, n: u.sem_perfil }));
    return (
      <>
        <MDBCard className="mb-3"><MDBCardBody>
          <MDBRow className="align-items-center">
            <MDBCol md="3" className="mb-2 mb-md-0">
              <div style={{ fontSize: '2.4rem', fontWeight: 600, lineHeight: 1 }}>{formataNumero(totais.ativos)}</div>
              <div className="text-muted" style={{ fontSize: '.85rem' }}>servidores ativos</div>
            </MDBCol>
            <MDBCol md="6" className="mb-2 mb-md-0">
              <BarraComposicaoTotal a={totais.forca} b={totais.demais} series={SERIES} />
            </MDBCol>
            <MDBCol md="3" className="text-md-right">
              <div style={{ fontSize: '1.6rem', fontWeight: 600, lineHeight: 1 }}>{formataNumero(totais.postos_fiscais)}</div>
              <div className="text-muted" style={{ fontSize: '.85rem' }}>
                postos fiscais · {formataNumero(totais.unidades_apoio)} unidades de apoio
              </div>
            </MDBCol>
          </MDBRow>
        </MDBCardBody></MDBCard>

        <MDBCard className="mb-3"><MDBCardBody>
          <h6 className="mb-2">Por unidade <small className="text-muted">(clique para ver as alas)</small></h6>
          <div className="painel-legenda mb-2">
            {SERIES.map((sr) => (
              <span key={sr.rotulo}><span className="painel-legenda__cor" style={{ background: sr.cor }} />{sr.rotulo}</span>
            ))}
          </div>
          <TabelaUnidades
            grupos={paraGrupos(unidades)}
            series={SERIES}
            total={{ a: totais.forca, b: totais.demais, total: totais.ativos }}
          />
        </MDBCardBody></MDBCard>

        <MDBRow className="mb-3">
          <MDBCol md="6" className="mb-2">
            <MDBCard className="h-100"><MDBCardBody>
              <h6 className="mb-3">Por órgão de origem</h6>
              <ListaBarras
                // Cor por item: força em azul, demais em cinza — a mesma convenção
                // do resto da tela. Antes tudo saía azul, e a Casa Civil (demais
                // servidores) parecia força.
                cor="var(--painel-b)"
                itens={orgaos.map((o) => ({
                  rotulo: rotuloOrgao(o), n: o.n, forca: o.forca, cor: o.forca ? 'var(--painel-a)' : undefined,
                }))}
                destaque={(i) => (i.forca ? 'força' : null)}
              />
            </MDBCardBody></MDBCard>
          </MDBCol>
          <MDBCol md="6" className="mb-2">
            <MDBCard className="h-100"><MDBCardBody>
              <h6 className="mb-3">Por função</h6>
              <ListaBarras itens={funcoes.map((f) => ({ rotulo: f.funcao, n: f.n }))} cor="var(--painel-b)" />
            </MDBCardBody></MDBCard>
          </MDBCol>
        </MDBRow>

        <MDBCard className="mb-3"><MDBCardBody>
          <h6 className="mb-3">Perfis de acesso</h6>
          {/* Adesão zero (#398): a barra virava um bloco cinza cheio, e "sem perfil,
              por unidade" repetia o "Por unidade" de cima. */}
          {totais.com_perfil === 0 ? (
            <p className="mb-0">
              Nenhum dos {formataNumero(totais.ativos)} servidores ativos tem perfil de acesso ainda. Os perfis se
              vinculam em Administração → Perfis de Acesso.
            </p>
          ) : (
            <>
              <BarraComposicaoTotal a={totais.com_perfil} b={totais.ativos - totais.com_perfil} series={SERIES_PERFIL} />
              <MDBRow className="mt-3">
                <MDBCol md="6" className="mb-2">
                  <div className="text-muted mb-2" style={{ fontSize: '.85rem' }}>Usuários por perfil</div>
                  <ListaBarras itens={perfis.map((p) => ({ rotulo: p.perfil, n: p.n }))} />
                </MDBCol>
                <MDBCol md="6" className="mb-2">
                  <div className="text-muted mb-2" style={{ fontSize: '.85rem' }}>Sem perfil, por unidade</div>
                  <ListaBarras itens={semPerfilPorUnidade} topN={6} cor="var(--painel-b)" />
                </MDBCol>
              </MDBRow>
              <small className="text-muted">Quem está sem perfil, pelo nome: Administração → Perfis de Acesso.</small>
            </>
          )}
        </MDBCardBody></MDBCard>

        <MDBCard className="mb-3"><MDBCardBody>
          <h6 className="mb-3">Qualidade do cadastro</h6>
          <ul className="list-unstyled mb-0">
            {PENDENCIAS.filter(([k]) => pendencias[k] !== null && pendencias[k] !== undefined).map(([k, txt]) => (
              <li key={k} className="d-flex justify-content-between border-bottom py-1">
                <span>
                  <MDBIcon
                    icon={pendencias[k] ? 'exclamation-triangle' : 'check'}
                    className="mr-2"
                    style={{ color: pendencias[k] ? 'var(--painel-alerta)' : 'var(--painel-ok-texto)' }}
                  />
                  {txt}
                </span>
                <strong style={{ color: pendencias[k] ? 'var(--painel-alerta)' : '#6c757d' }}>
                  {formataNumero(pendencias[k])}
                  <span className="sr-only">{pendencias[k] ? ' — corrigir' : ' — ok'}</span>
                </strong>
              </li>
            ))}
          </ul>
        </MDBCardBody></MDBCard>

      </>
    );
  };

  return (
    <>
      <NavBar />
      <MDBContainer className="my-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-0">Efetivo</h4>
            {dados && (
              <small className="text-muted">
                retrato de {formataDataHora(dados.gerado_em)} · escopo: {dados.escopo.gerencia}
              </small>
            )}
          </div>
          <div className="painel-acoes">
            <NotaMetodologica itens={NOTA} />
            <button type="button" className="painel-acao" onClick={carrega} disabled={carregando}>
              <MDBIcon icon="refresh" spin={carregando} />
              {carregando ? 'Atualizando…' : 'Atualizar'}
            </button>
          </div>
        </div>
        {corpo()}
      </MDBContainer>
    </>
  );
}
